'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps'
import { MapPin, Navigation, XIcon } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/combobox'
import { inputFocusClasses } from '@/components/ui/input'
import {
  DEFAULT_EVENT_NEAR_RADIUS_MILES,
  EVENT_NEAR_RADIUS_MILES,
  isUsZipCode,
} from '@/constants/eventNearRadius'
import { env } from '@/env'
import { useDebounce } from '@/hooks/useDebounce'
import { useEventsContext } from '@/providers/EventsProvider'
import {
  hasAnsweredExploreGeoPrompt,
  markExploreGeoDeclined,
  markExploreGeoGranted,
} from '@/utilities/events/exploreGeolocation'
import { cn } from '@/utilities/ui'

import type { EventNearRadiusMiles } from '@/constants/eventNearRadius'

type RadiusOption = {
  value: EventNearRadiusMiles
  label: string
}

const RADIUS_OPTIONS: RadiusOption[] = EVENT_NEAR_RADIUS_MILES.map((miles) => ({
  value: miles,
  label: `${miles} miles`,
}))

const queryInactionable = (query: string) => {
  return query.length < 2 || isUsZipCode(query) || query === 'Current location'
}

function LocationSearchInner() {
  const {
    searchLocation,
    setSearchLocation,
    clearSearchLocation,
    radiusMiles,
    setRadiusMiles,
    format,
  } = useEventsContext()
  const places = useMapsLibrary('places')
  const geocoding = useMapsLibrary('geocoding')
  const listboxId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  const [inputValue, setInputValue] = useState(searchLocation?.label ?? '')
  const [predictions, setPredictions] = useState<google.maps.places.PlacePrediction[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [zipError, setZipError] = useState<string | null>(null)
  const [radiusOpen, setRadiusOpen] = useState(false)
  const [geoPromptOpen, setGeoPromptOpen] = useState(false)
  const debouncedQuery = useDebounce(inputValue, 300)

  // The provider clears the applied search location when Virtual is selected,
  // but typed-but-unapplied text lives only in this component's state — reset
  // it during render when the format transitions to 'virtual'.
  const [prevFormat, setPrevFormat] = useState(format)
  if (format !== prevFormat) {
    setPrevFormat(format)
    if (format === 'virtual') {
      setInputValue('')
      setPredictions([])
      setZipError(null)
      setOpen(false)
    }
  }

  if (queryInactionable(debouncedQuery.trim())) {
    if (predictions.length > 0) {
      setPredictions([])
    }
    if (loading) {
      setLoading(false)
    }
  }

  // Everything the dropdown can render: the loading row, place predictions, or
  // the "Search ZIP" action. If none apply (e.g. a query with zero autocomplete
  // results, or an invalid ZIP), the dropdown must not stay open — close it
  // during render as soon as its content disappears.
  const hasDropdownContent = loading || predictions.length > 0 || isUsZipCode(inputValue)
  if (open && !hasDropdownContent) {
    setOpen(false)
  }

  const selectedRadius =
    RADIUS_OPTIONS.find((option) => option.value === radiusMiles) ??
    RADIUS_OPTIONS.find((option) => option.value === DEFAULT_EVENT_NEAR_RADIUS_MILES)!

  // Reflect the applied search location in the input only when it changes (the
  // provider creates a new object per applied search, so identity works here).
  // An unguarded sync would run on every render and wipe in-progress typing.
  const [prevSearchLocation, setPrevSearchLocation] = useState(searchLocation)
  if (searchLocation !== prevSearchLocation) {
    setPrevSearchLocation(searchLocation)
    setInputValue(searchLocation?.label ?? '')
  }

  const handleClearLocation = useCallback(() => {
    setInputValue('')
    setPredictions([])
    setOpen(false)
    setZipError(null)
    clearSearchLocation()
  }, [clearSearchLocation])

  // The opt-in modal is the only trigger for geolocation — even when browser permission is
  // already granted, we never request a position without the user consenting here first.
  useEffect(() => {
    if (searchLocation) return
    if (typeof window === 'undefined') return
    if (!('geolocation' in navigator)) return
    if (hasAnsweredExploreGeoPrompt()) return

    let cancelled = false

    const maybePrompt = async () => {
      try {
        const permission = await navigator.permissions?.query({ name: 'geolocation' })
        if (cancelled) return
        // Denied at the browser level needs no modal.
        if (permission?.state === 'denied') return
      } catch {
        // Permissions API unavailable — show modal if unanswered.
      }

      if (!cancelled && !hasAnsweredExploreGeoPrompt()) {
        setGeoPromptOpen(true)
      }
    }

    void maybePrompt()
    return () => {
      cancelled = true
    }
  }, [searchLocation])

  useEffect(() => {
    if (!places) return

    const query = debouncedQuery.trim()

    if (queryInactionable(query)) return

    let cancelled = false

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken()
    }

    void (async () => {
      setLoading(true)
      // Reopen for the loading row: the render-phase guard above closes the
      // dropdown while a fresh query has nothing to show yet.
      setOpen(true)
      try {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          includedRegionCodes: ['us'],
          sessionToken: sessionTokenRef.current ?? undefined,
        })
        if (cancelled) return
        const next = suggestions
          .map((suggestion) => suggestion.placePrediction)
          .filter((prediction): prediction is google.maps.places.PlacePrediction =>
            Boolean(prediction),
          )
        setPredictions(next)
        setOpen(next.length > 0)
      } catch {
        if (!cancelled) setPredictions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, places])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  const applyCoords = useCallback(
    (lat: number, lng: number, label: string) => {
      setZipError(null)
      setPredictions([])
      setOpen(false)
      setInputValue(label)
      setSearchLocation({ lat, lng, label })
      sessionTokenRef.current = null
    },
    [setSearchLocation],
  )

  const handleSelectPrediction = useCallback(
    (prediction: google.maps.places.PlacePrediction) => {
      void (async () => {
        const place = prediction.toPlace()
        await place.fetchFields({
          fields: ['location', 'displayName', 'formattedAddress', 'id'],
        })
        if (!place.location) return
        applyCoords(
          place.location.lat(),
          place.location.lng(),
          place.formattedAddress ?? place.displayName ?? prediction.text.text,
        )
      })()
    },
    [applyCoords],
  )

  const handleZipSearch = useCallback(
    (zip: string) => {
      if (!geocoding) return
      setLoading(true)
      setZipError(null)
      const geocoder = new geocoding.Geocoder()
      geocoder.geocode(
        { address: zip, componentRestrictions: { country: 'US' } },
        (results, status) => {
          setLoading(false)
          if (status !== 'OK' || !results?.[0]?.geometry?.location) {
            setZipError('Enter a valid US ZIP code')
            setPredictions([])
            setOpen(false)
            return
          }
          const location = results[0].geometry.location
          applyCoords(location.lat(), location.lng(), results[0].formatted_address || zip)
        },
      )
    },
    [applyCoords, geocoding],
  )

  const handleSubmitQuery = useCallback(() => {
    const query = inputValue.trim()
    if (!query) {
      clearSearchLocation()
      setZipError(null)
      return
    }
    if (isUsZipCode(query)) {
      handleZipSearch(query)
      return
    }
    if (predictions[0]) {
      handleSelectPrediction(predictions[0])
      return
    }
    setZipError('Select a place from the list, or enter a valid US ZIP code')
  }, [clearSearchLocation, handleSelectPrediction, handleZipSearch, inputValue, predictions])

  const handleGeoYes = useCallback(() => {
    markExploreGeoGranted()
    setGeoPromptOpen(false)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        applyCoords(position.coords.latitude, position.coords.longitude, 'Current location')
      },
      () => undefined,
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 15000 },
    )
  }, [applyCoords])

  const handleGeoNo = useCallback(() => {
    markExploreGeoDeclined()
    setGeoPromptOpen(false)
  }, [])

  return (
    <div className="flex w-full flex-col gap-2" ref={containerRef}>
      <h3 className="font-family-body text-base font-semibold text-muted-foreground">Location</h3>
      <div className="relative">
        <input
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          placeholder="Address or ZIP code"
          className={cn(
            'flex w-full rounded-xl border border-border bg-input py-3 pl-3 font-family-body text-base text-primary placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-reg',
            inputValue.trim() || searchLocation ? 'pr-20' : 'pr-10',
            inputFocusClasses,
          )}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setZipError(null)
            if (!e.target.value.trim() && searchLocation) {
              clearSearchLocation()
            }
            setOpen(e.target.value.trim() !== 'Current location')
          }}
          onFocus={() => {
            if (hasDropdownContent && inputValue.trim() !== 'Current location') setOpen(true)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSubmitQuery()
            }
          }}
        />
        {(inputValue.trim() || searchLocation) && (
          <button
            type="button"
            className="absolute top-1/2 right-10 -translate-y-1/2 rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Clear location search"
            onClick={handleClearLocation}
          >
            <XIcon className="size-4" />
          </button>
        )}
        <button
          type="button"
          className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Use current location"
          onClick={() => {
            markExploreGeoGranted()
            navigator.geolocation.getCurrentPosition(
              (position) => {
                applyCoords(position.coords.latitude, position.coords.longitude, 'Current location')
              },
              () => undefined,
              { enableHighAccuracy: false, maximumAge: 60_000, timeout: 15000 },
            )
          }}
        >
          <Navigation className="size-4" />
        </button>
        {open && hasDropdownContent && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-border bg-background py-1 shadow-md"
          >
            {loading && (
              <li className="px-3 py-2 font-body text-sm text-muted-foreground">Searching…</li>
            )}
            {predictions.map((prediction) => (
              <li key={prediction.placeId}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-3 py-2 text-left font-body text-sm hover:bg-muted"
                  onClick={() => handleSelectPrediction(prediction)}
                >
                  <span className="font-medium text-foreground">
                    {prediction.mainText?.text ?? prediction.text.text}
                  </span>
                  {prediction.secondaryText?.text ? (
                    <span className="text-xs text-muted-foreground">
                      {prediction.secondaryText.text}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
            {isUsZipCode(inputValue) && (
              <li className="border-t border-border">
                <button
                  type="button"
                  className="flex w-full px-3 py-2 text-left font-body text-sm font-medium text-foreground hover:bg-muted"
                  onClick={() => handleZipSearch(inputValue.trim())}
                >
                  Search ZIP {inputValue.trim()}
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
      {zipError && <p className="text-sm text-destructive">{zipError}</p>}

      <Combobox
        items={RADIUS_OPTIONS}
        value={selectedRadius}
        onValueChange={(item) => {
          if (!item) return
          setRadiusMiles((item as RadiusOption).value)
          setRadiusOpen(false)
        }}
        itemToStringLabel={(item) => (item as RadiusOption).label}
        itemToStringValue={(item) => String((item as RadiusOption).value)}
        isItemEqualToValue={(a, b) =>
          !!a && !!b && (a as RadiusOption).value === (b as RadiusOption).value
        }
        open={radiusOpen}
        onOpenChange={setRadiusOpen}
      >
        <ComboboxTrigger
          className={cn(
            'flex w-full items-center justify-between rounded-xl border border-border bg-input p-3 font-family-body text-base text-foreground md:text-reg',
            inputFocusClasses,
          )}
        >
          <span>{selectedRadius.label}</span>
        </ComboboxTrigger>
        <ComboboxContent className="w-(--anchor-width)" align="start">
          <ComboboxList>
            {(item: RadiusOption) => (
              <ComboboxItem key={item.value} value={item}>
                {item.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>

      <AlertDialog open={geoPromptOpen} onOpenChange={setGeoPromptOpen}>
        <AlertDialogContent size="sm" overlayVariant="blur">
          <AlertDialogHeader>
            <AlertDialogMedia className="rounded-full bg-evergreen-50 text-evergreen-700">
              <MapPin />
            </AlertDialogMedia>
            <AlertDialogTitle>Use your current location?</AlertDialogTitle>
            <AlertDialogDescription>
              Allow location access to find events near you. You can also search by address or ZIP
              code anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="accent" color="inverse" icon={false} onClick={handleGeoNo}>
                No
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button onClick={handleGeoYes}>Yes</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default function LocationSearch() {
  return (
    <APIProvider apiKey={env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}>
      <LocationSearchInner />
    </APIProvider>
  )
}
